// src/pages/Sanitary/SanitaryEventsPage.jsx
// =============================================================================
// Sanitary hygiene events register
// =============================================================================

import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import EvaluationPeriodPicker from "../../components/filters/EvaluationPeriodPicker";
import useEvaluationPeriod from "../../hooks/useEvaluationPeriod";
import { EVALUATION_PERIOD_TYPES } from "../../utils/evaluationPeriodUtils";
import { fetchSanitaryHygieneEvents } from "../../services/sanitaryEventService";

import "../Stock/StockMasterDataPage.css";
import "./SanitaryProductsPage.css";

function asArray(value) { return Array.isArray(value) ? value : []; }
function formatQty(qty) {
  if (qty === undefined || qty === null || qty === "") return "—";
  const n = Number(qty);
  if (!Number.isFinite(n)) return String(qty);
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
}
function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString();
}
function productLabel(event) {
  const p = event?.product || {};
  return [p.name_public, p.sku ? `SKU ${p.sku}` : null].filter(Boolean).join(" · ") || "—";
}
function locationLabel(event) {
  const l = event?.location || null;
  return l ? [l.code, l.name].filter(Boolean).join(" · ") || "—" : "—";
}
function ruleLabel(event) {
  const r = event?.rule || {};
  return [r.code, r.name].filter(Boolean).join(" · ") || (event?.rule_id ? `#${event.rule_id}` : "—");
}
function eventTypeLabel(t, type) { return t(`sanitary:events.eventTypes.${type}`, { defaultValue: type || "—" }); }
function statusLabel(t, status) { return t(`sanitary:events.statuses.${status}`, { defaultValue: status || "—" }); }
function technicalErrorDetail(err) {
  const status = err?.status ? `HTTP ${err.status}` : "";
  const code = err?.code ? String(err.code) : "";
  return [status, code].filter(Boolean).join(" / ");
}

export default function SanitaryEventsPage() {
  const { t } = useTranslation(["sanitary", "common"]);
  const period = useEvaluationPeriod({
    defaultMode: EVALUATION_PERIOD_TYPES.DAY,
    initialDate: new Date(),
    allowedModes: [
      EVALUATION_PERIOD_TYPES.DAY,
      EVALUATION_PERIOD_TYPES.WEEK,
      EVALUATION_PERIOD_TYPES.MONTH,
      EVALUATION_PERIOD_TYPES.THREE_MONTHS,
      EVALUATION_PERIOD_TYPES.SIX_MONTHS,
      EVALUATION_PERIOD_TYPES.YEAR,
      EVALUATION_PERIOD_TYPES.MANUAL,
    ],
  });
  const selectedPeriod = useMemo(() => ({ period_from: period.from, period_to: period.to }), [period.from, period.to]);
  const [filters, setFilters] = useState({ event_type: "" });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadData = async (nextFilters = filters, nextPeriod = selectedPeriod) => {
    setLoading(true); setError("");
    try {
      const rows = await fetchSanitaryHygieneEvents({ ...nextPeriod, ...nextFilters, limit: 1000 });
      setEvents(asArray(rows)); setHasLoaded(true);
    } catch (err) {
      const detail = technicalErrorDetail(err);
      setError(detail ? t("sanitary:events.messages.loadFailedWithDetail", { detail }) : t("sanitary:events.messages.loadFailed"));
    } finally { setLoading(false); }
  };

  useEffect(() => { loadData(filters, selectedPeriod); }, []);

  return (
    <div className="stock-md-page sanitary-page">
      <div className="stock-md-header"><div><div className="stock-md-eyebrow">{t("sanitary:events.eyebrow")}</div><h1>{t("sanitary:events.title")}</h1><p>{t("sanitary:events.subtitle")}</p></div></div>
      {error && <div className="stock-md-error">{error}</div>}
      {!loading && !error && hasLoaded && events.length === 0 && <div className="stock-md-info-box">{t("sanitary:events.messages.emptyState")}</div>}

      <section className="stock-md-card">
        <div className="stock-md-card-header"><div><h2>{t("sanitary:events.filters.title")}</h2><p>{t("sanitary:events.filters.subtitle")}</p></div></div>
        <form className="stock-md-toolbar sanitary-period-filter-row" onSubmit={(e) => { e.preventDefault(); loadData(filters, selectedPeriod); }}>
          <EvaluationPeriodPicker period={period} disabled={loading} modeLabel={t("sanitary:common.period.mode", { defaultValue: "Typ okresu" })} fromLabel={t("sanitary:common.period.from", { defaultValue: "Od" })} toLabel={t("sanitary:common.period.to", { defaultValue: "Do" })} displayLabel={t("sanitary:common.period.selected", { defaultValue: "Wybrany okres" })} />
          <label className="stock-md-field"><span>{t("sanitary:events.filters.eventType")}</span><select value={filters.event_type || ""} onChange={(e) => setFilters((prev) => ({ ...prev, event_type: e.target.value }))}>
            <option value="">{t("common:all", { defaultValue: "All" })}</option>
            {['solution_prepared', 'consumption_confirmed', 'consumption_adjusted', 'consumption_rejected', 'stock_posted'].map((type) => <option key={type} value={type}>{eventTypeLabel(t, type)}</option>)}
          </select></label>
          <button type="submit" className="stock-md-button stock-md-button--secondary" disabled={loading}>{t("sanitary:events.actions.refresh")}</button>
        </form>
      </section>

      <section className="stock-md-card">
        <div className="stock-md-card-header"><div><h2>{t("sanitary:events.table.title")}</h2><p>{t("sanitary:events.table.subtitle")}</p></div></div>
        <div className="stock-md-table-wrap"><table className="stock-md-table stock-md-table--compact"><thead><tr>
          <th>{t("sanitary:events.table.datetime")}</th><th>{t("sanitary:events.table.type")}</th><th>{t("sanitary:events.table.status")}</th><th>{t("sanitary:events.table.product")}</th><th>{t("sanitary:events.table.location")}</th><th>{t("sanitary:events.table.rule")}</th><th>{t("sanitary:events.table.quantity")}</th><th>{t("sanitary:events.table.entry")}</th><th>{t("sanitary:events.table.stockMovement")}</th><th>{t("sanitary:events.table.comment")}</th>
        </tr></thead><tbody>
          {events.length === 0 ? <tr><td className="stock-md-empty" colSpan="10">{loading ? t("sanitary:events.messages.loading") : t("sanitary:events.table.empty")}</td></tr> : events.map((event) => <tr key={event.id}>
            <td><strong>{event.business_date || "—"}</strong><div className="stock-md-muted">{formatDateTime(event.event_datetime)}</div></td><td>{eventTypeLabel(t, event.event_type)}</td><td>{statusLabel(t, event.status)}</td><td>{productLabel(event)}</td><td>{locationLabel(event)}</td><td>{ruleLabel(event)}</td><td>{event.quantity == null ? "—" : `${formatQty(event.quantity)} ${event.unit_code || ""}`}</td><td>{event.consumption_entry_id ? `#${event.consumption_entry_id}` : "—"}</td><td>{event.stock_movement_id ? `#${event.stock_movement_id}` : "—"}</td><td>{event.comment || "—"}</td>
          </tr>)}
        </tbody></table></div>
      </section>
    </div>
  );
}
